import { tryRefreshToken } from '@/shared/libs/authSession';
import { authStorage } from '@/shared/libs/authStorage';
import { buildHttpError } from '@/shared/libs/httpError';
import logger from '@/shared/libs/logger.client';
import { orgScope } from '@/shared/libs/orgScope';
import { previewScope } from '@/shared/libs/previewScope';
import { currentTenantSlug } from '@/shared/libs/tenantHost';
import { useAuthStore } from '@/shared/store/authStore';
import { normalizeHeaders } from '@/shared/utils/authHelpers';

if (!process.env.NEXT_PUBLIC_API_BASE_URL) {
  throw new Error('NEXT_PUBLIC_API_BASE_URL is not defined in the environment');
}

/**
 * A secured HTTP client wrapper around `fetch` with built-in authentication.
 *
 * Features:
 * - Automatically attaches the `Authorization` header if an access token is available.
 * - Handles token refresh transparently when receiving a `401 Unauthorized` response.
 * - Distinguishes between internal (`/api/`) and external API calls by building the correct URL.
 * - Normalizes headers and ensures `Content-Type: application/json` is set by default.
 * - Provides consistent error handling (parses JSON error body or returns raw text).
 *
 * @template T - Expected response type.
 * @param endpoint - The API endpoint (internal `/api/...` or external `/v1/...`).
 * @param options - Fetch options (method, headers, body, etc.).
 * @returns A promise that resolves to the parsed JSON response of type `T`.
 * @throws Will throw an error if the network request fails, if the session is expired, or if the response is not OK.
 */
export async function httpClient<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const headers = normalizeHeaders(options.headers);
  const body = options.body;
  const isFormData =
    typeof FormData !== 'undefined' &&
    body !== undefined &&
    body !== null &&
    body instanceof FormData;
  if (!isFormData) {
    headers['Content-Type'] = headers['Content-Type'] ?? 'application/json';
  }

  // Source de l'action pour le journal d'audit BE (mobile | web).
  headers['x-client'] = headers['x-client'] ?? 'web';

  // Identifiant de corrélation : un même id navigateur → BFF → backend, pour
  // relier une ligne de log terminal à l'entrée backend correspondante.
  if (!headers['x-request-id'] && typeof crypto !== 'undefined' && crypto.randomUUID) {
    headers['x-request-id'] = crypto.randomUUID();
  }

  // Aperçu démo (admin) : un jeton read-only sample-only prime sur le token de
  // session pour renvoyer les données du consultant fictif. Absent hors aperçu.
  const previewToken = previewScope.getToken();

  // Retrieve your accessToken from localStorage/cookies
  const accessToken = previewToken ?? authStorage.getAccessToken();
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  // Impersonation console plateforme : scope l'org active (le BE ne l'honore
  // que pour un PLATFORM_ADMIN, l'ignore sinon). Non défini pour un user normal.
  const scopedOrgId = orgScope.getOrgId();
  if (scopedOrgId) {
    headers['X-Org-Id'] = scopedOrgId;
  }

  // Multi-tenant par sous-domaine : porte le tenant courant (<slug>.suivora-app.com).
  // Le BE l'exploite surtout au login/flux publics (email unique PAR org) ; une
  // fois connecté, l'org vient du JWT. Absent sur la console éditeur et en dev
  // mono-domaine (localhost).
  const tenantSlug = currentTenantSlug();
  if (tenantSlug) {
    headers['X-Tenant-Slug'] = tenantSlug;
  }

  let response: Response;
  const isInternal = endpoint.startsWith('/api/');
  const base = process.env.NEXT_PUBLIC_API_BASE_URL!.replace(/\/+$/, '');
  const path = endpoint.replace(/^\/+/, '');
  const url = isInternal ? endpoint : `${base}/${path}`;

  try {
    response = await fetch(url, {
      ...options,
      headers,
    });
  } catch (error) {
    logger.error('[httpClient] Network error', { error });
    throw new Error('Impossible de contacter le serveur. Vérifiez votre connexion.');
  }

  // Handle refresh token logic. En aperçu démo, le jeton est éphémère et sans
  // refresh : un 401 signifie « aperçu expiré » — ne pas rafraîchir la session
  // admin (qui bouclerait), laisser l'erreur remonter.
  if (
    response.status === 401 &&
    !previewToken &&
    endpoint !== '/api/v1/auth/login' &&
    endpoint !== '/api/v1/auth/refresh-token'
  ) {
    // Le BE renvoie un `code` machine (BE #365) qui pilote l'action, sans string-matching :
    // - SESSION_EXPIRED  → access token expiré : tenter un refresh puis rejouer la requête ;
    // - AUTH_REQUIRED    → aucun token envoyé : logout direct (inutile de rafraîchir) ;
    // - SESSION_INVALID  → token malformé/altéré : logout direct.
    // Repli si pas de `code` (BE pas encore déployé) : ancien comportement (on tente le refresh).
    let code: string | undefined;
    let message: string | undefined;
    try {
      const body = (await response.clone().json()) as { code?: unknown; message?: unknown };
      if (typeof body.code === 'string') code = body.code;
      if (typeof body.message === 'string') message = body.message;
    } catch {
      // corps non-JSON : on retombe sur le comportement par défaut ci-dessous.
    }

    const shouldRefresh = code === undefined || code === 'SESSION_EXPIRED';
    if (shouldRefresh) {
      const refreshed = await tryRefreshToken();
      if (refreshed.ok) {
        return httpClient<T>(endpoint, options);
      }
    }
    // Refresh non tenté (AUTH_REQUIRED / SESSION_INVALID) ou échoué → fin de session.
    authStorage.clearTokens();
    useAuthStore.getState().clearSession();
    const sessionError = new Error(
      message || 'Session expirée. Veuillez vous reconnecter.',
    ) as Error & { status?: number; code?: string };
    sessionError.status = 401;
    if (code) sessionError.code = code;
    throw sessionError;
  }

  // Handle non-OK responses
  if (!response.ok) {
    /* Construite au même endroit que celle du client public (#2228) : les deux copies avaient
       divergé, et celle du public jetait le `code` machine du serveur. */
    throw buildHttpError(response.status, await response.text());
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const contentLength = response.headers.get('content-length');
  if (contentLength === '0') {
    return undefined as T;
  }

  const rawBody = await response.text();
  if (!rawBody) {
    return undefined as T;
  }

  return JSON.parse(rawBody) as T;
}
